// Generated with util/create-component.js
import React, { useContext, useEffect, useState, useRef, useCallback, MouseEvent, KeyboardEvent } from 'react';
import { notification, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';

import { PropertySettingsActionsContext, PropertySettingsDataContext } from 'containers/data_context';

import GiftCardsStoreSettingsForm from 'components/gift_cards_store_settings_form/gift_cards_store_settings_form';
import Loading from 'components/loading/loading';
import { IStoreSettings } from 'components/gift_cards_store_settings_form/gift_cards_store_settings_form.types';

import styles from './gift_cards_store_settings_page.module.scss';

const defaultStoreSettings = {
  bannerParagraph: '',
  bannerTitle: '',
  cornerStyle: 'rounded',
  tagColor: '#0F1015',
  termsAndConditions: '',
  bookingTermsAndConditions: '',
  textFont: '',
  titleFont: '',
  slugName: '',
  name: '',
  allowCancellation: false,
  cancellationDeadlineDays: 1,
  status: 'active',
  gaTrackingId: '',
  purchaseConfirmationEmail: '',
  allowPickupAtHotel: false,
  allowPickupAtHotelInfo: '',
  storeGiftCardTypes: [],
};

const GiftCardsStoreSettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const newTabIndex = useRef(0);
  const propertySettingsData = useContext(PropertySettingsDataContext);
  const [activeKey, setActiveKey] = useState<string>();
  const [items, setItems] = useState<TabsProps['items']>([]);
  const [hasLoadedStoreData, setHasLoadedStoreData] = useState<boolean>(false);
  const [currentSelectedProperty, setCurrentSelectedProperty] = useState<string>();

  const {
    loadSuppliersList,
    loadPropertyGiftCardsStores,
    loadPropertyGiftCardsStore,
    createPropertyGiftCardsStore,
    updatePropertyGiftCardsStore,
  } = useContext(PropertySettingsActionsContext);

  const {
    selectedProperty,
    isUpdatingGiftCardsStoreSettings,
    propertyGiftCardsStores: { data: propertyGiftCardsStoresData, isLoading: isLoadingGiftCardsStores },
    propertyGiftCardsStore: { data: propertyGiftCardsStoreData },
  } = propertySettingsData;

  useEffect(() => {
    async function initApp() {
      if (selectedProperty) {
        setCurrentSelectedProperty(selectedProperty);
        try {
          await loadPropertyGiftCardsStores(selectedProperty);
        } catch (e) {
          console.error('error', e);
        }
      }
    }
    initApp();
  }, []);

  useEffect(() => {
    async function loadInitialStore() {
      const selectedPropertyChanged = selectedProperty !== currentSelectedProperty;
      if ((!hasLoadedStoreData || selectedPropertyChanged) && propertyGiftCardsStoresData?.length > 0) {
        try {
          setHasLoadedStoreData(true);
          if (selectedPropertyChanged) {
            setCurrentSelectedProperty(selectedProperty);
          }
          await loadStoreData(propertyGiftCardsStoresData[0].id);
        } catch (e) {
          console.error('error', e);
        }
      }
    }
    loadInitialStore();
  }, [propertyGiftCardsStoresData, hasLoadedStoreData, selectedProperty]);

  useEffect(() => {
    const items = getItems();
    setItems(items);
    setActiveKey(items[0].key);
  }, [propertyGiftCardsStoresData]);
  useEffect(() => {
    const updatedItems: TabsProps['items'] = items?.map(item => {
      if (item.key === activeKey) {
        return {
          ...item,
          children: (
            <GiftCardsStoreSettingsForm
              storeSettings={formatStorySettings(propertyGiftCardsStoreData)}
              isUpdatingStoreSettings={isUpdatingGiftCardsStoreSettings}
              onUpdateStoreSettings={handleUpdateStoreSettings}
            />
          ),
        };
      }
      return item;
    });
    setItems(updatedItems);
  }, [propertyGiftCardsStoreData]);
  const loadStoreData = useCallback(
    async (storeId: number) => {
      try {
        await loadPropertyGiftCardsStore({ propertyId: selectedProperty, storeId });
      } catch (e) {
        console.error('error', e);
      }
    },
    [loadPropertyGiftCardsStore, selectedProperty],
  );

  const handleUpdateStoreSettings = async (storeSettings: FormData) => {
    try {
      const storeId = storeSettings.get('store_params[store_setting_attributes][id]');
      const isNewStore = !storeId;

      if (isNewStore) {
        await createPropertyGiftCardsStore({ propertyId: selectedProperty, storeSettings });
      } else {
        await updatePropertyGiftCardsStore({ propertyId: selectedProperty, storeId: storeId, storeSettings });
      }
      openNotificationWithIcon('success');
      loadSuppliersList();
    } catch (e) {
      const error = e as Error;

      if (error.message === 'Validation failed: Name has already been taken') {
        openNotificationWithIcon('nameTaken');
      } else {
        openNotificationWithIcon('error');
      }
    }
  };

  const getItems = useCallback(() => {
    if (propertyGiftCardsStoresData === null || propertyGiftCardsStoresData?.length === 0) {
      return [
        {
          label: t('gift_card.store_default'),
          children: (
            <GiftCardsStoreSettingsForm
              storeSettings={defaultStoreSettings}
              isUpdatingStoreSettings={isUpdatingGiftCardsStoreSettings}
              onUpdateStoreSettings={handleUpdateStoreSettings}
            />
          ),
          key: '1',
          closable: false,
        },
      ];
    } else {
      return (
        propertyGiftCardsStoresData &&
        propertyGiftCardsStoresData?.map((store: IStoreSettings) => {
          const settings =
            store.id === propertyGiftCardsStoreData?.id
              ? formatStorySettings(propertyGiftCardsStoreData)
              : formatStorySettings(store);
          const children =
            store.id === propertyGiftCardsStoreData?.id ? (
              <GiftCardsStoreSettingsForm
                storeSettings={settings}
                isUpdatingStoreSettings={isUpdatingGiftCardsStoreSettings}
                onUpdateStoreSettings={handleUpdateStoreSettings}
              />
            ) : (
              <Loading size={34} />
            );
          return {
            label: store.name,
            children: children,
            key: `${store.id}`,
            closable: false,
          };
        })
      );
    }
  }, [propertyGiftCardsStoresData, isUpdatingGiftCardsStoreSettings, handleUpdateStoreSettings]);

  const openNotificationWithIcon = (type: 'success' | 'error' | 'nameTaken') => {
    if (type === 'success') {
      return notification['success']({
        message: t('general.success_message'),
      });
    }
    if (type === 'error') {
      return notification['error']({
        message: t('general.error_message'),
        description: t('general.error_description'),
      });
    }
    if (type === 'nameTaken') {
      return notification['error']({
        message: t('gift_card.slug_taken'),
        description: t('gift_card.slug_taken_description'),
      });
    }
  };

  const formatStorySettings = (settings: IStoreSettings) => {
    const formattedSettings = settings ? JSON.parse(JSON.stringify(settings)) : {};
    delete formattedSettings?.photoUrl;
    if (settings?.photoUrl) {
      formattedSettings.photo = {
        name: 'bannerPhoto.png',
        url: settings.photoUrl,
      };
    }
    if (settings?.logoUrl) {
      formattedSettings.logo = {
        name: 'hotelLogo.png',
        url: settings.logoUrl,
      };
    }

    return formattedSettings;
  };

  const onTabChange = (newActiveKey: string) => {
    setActiveKey(newActiveKey);
    if (!newActiveKey.includes('newTab')) {
      loadStoreData(Number(newActiveKey));
    }
  };

  const addStore = () => {
    const newActiveKeyCount = newTabIndex.current++;
    const newActiveKey = `newTab${newActiveKeyCount}`;
    const newPanes = items?.length ? [...items] : [];
    newPanes.push({
      label: t('gift_card.store_new'),
      children: (
        <GiftCardsStoreSettingsForm
          storeSettings={defaultStoreSettings}
          isUpdatingStoreSettings={isUpdatingGiftCardsStoreSettings}
          onUpdateStoreSettings={handleUpdateStoreSettings}
        />
      ),
      key: newActiveKey,
      closable: true,
    });
    setItems(newPanes);
    setActiveKey(newActiveKey);
  };

  const removeTab = (targetKey: string | MouseEvent<Element> | KeyboardEvent<Element>) => {
    let newActiveKey = activeKey;
    let lastIndex = -1;
    items?.forEach((item, i) => {
      if (item.key === targetKey) {
        lastIndex = i - 1;
      }
    });
    const newPanes = items?.filter(item => item.key !== targetKey);
    if (newPanes?.length && newActiveKey === targetKey) {
      if (lastIndex >= 0) {
        newActiveKey = newPanes[lastIndex].key;
      } else {
        newActiveKey = newPanes[0].key;
      }
    }
    setItems(newPanes);
    setActiveKey(newActiveKey);
  };
  const onEdit = (targetKey: string | MouseEvent<Element> | KeyboardEvent<Element>, action: 'add' | 'remove') => {
    if (action === 'add') {
      addStore();
    } else {
      removeTab(targetKey);
    }
  };

  if (isLoadingGiftCardsStores) {
    return <Loading size={34} />;
  }

  return (
    <div data-testid="GiftCardsStoreSettingsPage" className={styles.root}>
      <Tabs type="editable-card" items={items} activeKey={activeKey} onChange={onTabChange} onEdit={onEdit} />
    </div>
  );
};

export default GiftCardsStoreSettingsPage;
